import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { router } from '@inertiajs/react'
import { Mail, XCircle } from 'lucide-react'

interface GmailBadgeProps {
  gmailConnection: {
    id: number
    email: string
    syncStatus: string
    active: boolean
  } | null
}

export default function GmailBadge({ gmailConnection }: GmailBadgeProps) {
  const [isProcessing, setIsProcessing] = useState(gmailConnection?.active ?? false)
  const [isToggling, setIsToggling] = useState(false)

  const handleToggleProcessing = async () => {
    if (isToggling) return

    setIsToggling(true)
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

      const response = await fetch('/gmail_connections/toggle_processing', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || '',
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const errorMessage = errorData.error || 'Failed to toggle processing'
        throw new Error(errorMessage)
      }

      const data = await response.json()
      setIsProcessing(data.active)
    } catch (error) {
      console.error('Failed to toggle processing:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to toggle email processing. Please try again.'
      alert(errorMessage)
      // Reset the toggle state to match the actual server state
      setIsProcessing(gmailConnection?.active ?? false)
    } finally {
      setIsToggling(false)
    }
  }

  const handleDisconnect = () => {
    if (confirm('Are you sure you want to disconnect your Gmail account?')) {
      router.delete(`/gmail_connections/${gmailConnection?.id}`, {
        preserveScroll: true,
      })
    }
  }

  if (!gmailConnection) {
    return (
      <Button asChild variant="outline">
        <a href="/gmail_connections/connect">
          <Mail className="w-4 h-4 mr-2" />
          Connect Gmail
        </a>
      </Button>
    )
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg px-4 py-2 w-full sm:w-auto">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        {/* Email + status dot */}
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${isProcessing ? 'bg-green-500' : 'bg-gray-400'}`} />
          <Mail className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          <span className="text-sm font-medium text-blue-950 dark:text-gray-100">{gmailConnection.email}</span>
        </div>

        {/* Toggle switch and disconnect button */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Toggle switch with icons */}
          <Switch
            id="processing-toggle"
            checked={isProcessing}
            onCheckedChange={handleToggleProcessing}
            disabled={isToggling}
          />

          {/* Disconnect button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDisconnect}
            className="h-8 p-2"
            title="Disconnect Gmail"
          >
            <XCircle className="w-4 h-4 text-gray-400 dark:text-gray-500" />
          </Button>
        </div>
      </div>
    </div>
  )
}
